import {
    AuthoringBlockType, BlockStructureType, BlockStyleType, ContentBlockType,
    HorizontalAlignType, TextStyleType,
    VerticalAlignType
} from "common/constants";
import { ds } from "js/core/models/dataService";
import * as geom from "js/core/utilities/geom";
import { _ } from "js/vendor";
import React from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { BaseElement } from "../../base/BaseElement";
import { CollectionElement, CollectionItemElement } from "../../base/CollectionElement";
import {
    calcAuthoringBlockProps, calculateEvenBreakTextWidth
} from "../../base/Text/TextLayoutHelpers";
import {
    AuthoringBlockContainer
} from "../../elements/authoring/AuthoringBlockContainer";
import { BigNumbers } from "../../elements/BigNumbers";
import CompareRadialBar from "../../elements/compare/CompareRadialBar";
import { Dashboard } from "../../elements/Dashboard";
import { GanttChart } from "../../elements/GanttChart";
import { Headline } from "../../elements/Headline";
import { IconTextGrid } from "../../elements/Lists/IconsList";
import { PhotoCollage } from "../../elements/PhotoCollage";
import { TableFrame } from "../../elements/Table/Table";
import { Timeline } from "../../elements/Timeline";
import { FindBestFit } from "../../layouts/FindBestFit";

const TextBlockContainer = styled.div`
  position: relative;
`;

// class BlockContainer extends Component {
//
//     componentDidMount() {
//
//     }
//
//     render() {
//         return (
//             <div style={{position: "relative"}}>
//                 {this.props.children}
//             </div>
//         );
//     }
// }

export class DocContainerElement extends BaseElement {
    _canSelect() {
        return true;
    }

    get selectionUIType() {
        return "DocContainerElementSelection";
    }

    get isOnDocumentCanvas() {
        return false;
    }

    setupElement() {
        this.blockContainerRef = React.createRef();

        this.intersectionObserver = new IntersectionObserver(entries => {
            console.log(entries);
        }, {});
    }

    getSelectionElement() {
        return this;
    }

    get bindTo() {
        return this.options.bindTo || "text";// this.id;
    }

    get defaultBlockTextStyle() {
        return this.options.defaultBlockTextStyle || TextStyleType.TITLE;
    }

    get blockStructure() {
        return BlockStructureType.FREEFORM;
        // return this.options.blockStructure || BlockStructureType.SINGLE_BLOCK;
    }

    get allowListStyling() {
        return this.options.allowListStyling ?? false;
    }

    get autoWidth() {
        return false;
    }

    get autoHeight() {
        return false;
    }

    get textModel() {
        // if the element was created with an html option, it doesn't use the model so we just generate a fake model
        if (this.options.html) {
            return {
                blocks: [{
                    id: "block",
                    html: this.options.html.toString(),
                    type: AuthoringBlockType.TEXT,
                    textStyle: "text"
                }]
            };
        }

        let model = this.model[this.bindTo];

        if (!model && this.options.defaultValue) {
            return {
                blocks: [{
                    html: this.options.defaultValue,
                    type: AuthoringBlockType.TEXT,
                }]
            };
        }

        if (!model?.blocks) {
            let blockTextStyle = this.options.defaultBlockTextStyle;
            if (!blockTextStyle) {
                switch (this.blockStructure) {
                    case BlockStructureType.FREEFORM:
                    case BlockStructureType.HEADER:
                        blockTextStyle = TextStyleType.HEADING;
                        break;
                    case BlockStructureType.SINGLE_BLOCK:
                    case BlockStructureType.TITLE_AND_BODY:
                    default:
                        blockTextStyle = TextStyleType.TITLE;
                        break;
                }
            }

            let html;
            if (typeof this.model[this.bindTo] == "object") {
                // support legacy model where value was an object with .text property
                html = this.model[this.bindTo].text ?? "";
            } else {
                html = this.model[this.bindTo]?.toString() ?? "";
            }

            this.model[this.bindTo] = {
                blocks: [{
                    id: uuid(),
                    html,
                    type: AuthoringBlockType.TEXT,
                    textStyle: blockTextStyle
                }]
            };
            model = this.model[this.bindTo];
        }

        return model;
    }

    // add a new block to the model
    addBlock(options) {
        let block = {
            id: uuid(),
            type: options.type || AuthoringBlockType.TEXT,
            html: options.html || "",
            textStyle: options.textStyle
        };
        if (options.index !== undefined) {
            this.textModel.blocks.insert(block, options.index);
        } else {
            this.textModel.blocks.push(block);
        }
        return block;
    }

    // get a react ref to a block
    getBlockRef(blockId) {
        return this.blockContainerRef.current.blockRefs[blockId].current;
    }

    getBlockById(blockId) {
        return this.textModel.blocks.findById(blockId);
    }

    getBlockByTextStyle(textStyle) {
        return this.textModel.blocks.find(block => block.textStyle == textStyle);
    }

    get blockGap() {
        return this.styles.gap ?? 30;
    }

    async _load() {
    }

    get textAlign() {
        return this.model.textAlign || this.options.textAlign || this.styles.textAlign || HorizontalAlignType.LEFT;
    }

    get verticalAlign() {
        return this.model.verticalAlign || this.options.verticalAlign || this.styles.verticalAlign || VerticalAlignType.TOP;
    }

    get minTextScale() {
        return 0.5;
    }

    getPlaceholderForBlock(block) {
        if (this.blockStructure == BlockStructureType.SINGLE_BLOCK && this.options.placeholder) {
            return this.options.placeholder;
        }
        return `Type ${this.getTextStyleForBlock(block)}`;
    }

    getTextStyleForBlock(block) {
        let textStyleType;
        if (this.blockStructure == BlockStructureType.SINGLE_BLOCK) {
            textStyleType = this.id;
        } else {
            textStyleType = block.textStyle || block.id;
        }
        return textStyleType;
    }

    getTextStylesForBlock(blockId) {
        let block = this.textModel.blocks.findById(blockId) || this.textModel.blocks[0];

        if (block.type !== AuthoringBlockType.TEXT) {
            return {};
        }
        // get the styles for the text by either the model's textStyle
        // or if no textStyle exists, use the id of the item
        // and if no specific styles are defined for the text style, just use the root styles

        let textStyleType = block.textStyle || this.id;
        let textStyles = this.styles[textStyleType];
        if (!textStyles) {
            textStyles = this.styles;
        }

        let styles = {
            fontFamily: textStyles.fontId,
            fontSize: textStyles.fontSize,
            fontColor: textStyles.fontColor,
            fontScaling: textStyles.fontScaling,
            fontWeight: textStyles.fontWeight,
            letterSpacing: textStyles.letterSpacing,
            lineHeight: textStyles.lineHeight,
            textTransform: textStyles.textTransform,
            marginTop: textStyles.marginTop ?? 0,
            marginBottom: textStyles.marginBottom ?? 0,
            marginLeft: textStyles.marginLeft ?? 0,
            marginRight: textStyles.marginRight ?? 0,
            // boldStyles: textStyles.bold,
            // maxLines: textStyles.maxLines,
            // evenBreak,
            paragraphSpacing: textStyles.paragraphSpacing ?? 0
        };

        // adjust fontSize by fontScaling style property (from theme font settings)
        styles.fontSize *= ((styles.fontScaling || 100) / 100);

        // use avaialble model overrides for these style properties
        styles.fontSize = block.fontSize ?? styles.fontSize;
        styles.lineHeight = block.lineHeight ?? styles.lineHeight;
        styles.letterSpacing = block.letterSpacing ?? styles.letterSpacing;

        styles.textAlign = this.textAlign;

        if (styles.textTransform == "auto") {
            styles.textTransform = "none"; // convert to valid css value
        }

        if (this.options.syncFontSizeWithSiblings) {
            const collectionElement = this.findClosestOfType(CollectionElement);
            for (const item of collectionElement.itemElements) {
                const blocks = item.model[this.bindTo]?.blocks;
                if (blocks) {
                    const blockModel = blocks.find(b => b.textStyle == textStyleType);
                    if (blockModel && blockModel.fontSize) {
                        styles.fontSize = blockModel.fontSize;
                        break;
                    }
                }
            }
        }

        return styles;
    }

    _build() {
        this.blockElements = {};
        for (let block of this.textModel.blocks) {
            if (block.type == AuthoringBlockType.ELEMENT) {
                if (!block.elementModel) {
                    block.elementModel = {
                        elementType: "PhotoCollage"
                    };
                }

                // let documentElement = this.addElement(block.id, () => DocumentElementContainer, {
                //     model: block.elementModel ,
                //     blockId: block.id,
                // });
                // this.blockElements[block.id] = documentElement;

                let elementClass = this.canvas.elementManager.get(block.elementModel.elementType);
                let element = this.addElement(block.id, () => elementClass, {
                    model: block.elementModel,
                    blockId: block.id,
                    canSelect: false
                });
                this.blockElements[block.id] = element;
            }
        }
    }

    _calcProps(props, options) {
        let { size } = props;

        let autoWidth = options.autoWidth ?? this.autoWidth;
        let autoHeight = options.autoHeight ?? this.autoHeight;

        let columns = options.columns || 1;
        let columnGap = this.styles.columnGap ?? 20;

        let textScale = 1;
        if (options.forceTextScale && options.forceTextScale != 1) {
            textScale = options.forceTextScale;
        }

        let blockProps = [];
        let containerSize;
        let isTextFit = true;

        if (options.scaleTextToFit) {
            let { isFit, fitValue: scale, scaledBlockProps, scaledContainerSize } = FindBestFit({
                min: this.minTextScale,
                max: 1,
                layout: scale => {
                    let { containerSize, blockProps } = calcAuthoringBlockProps({
                        element: this,
                        model: this.textModel,
                        width: size.width,
                        autoWidth, autoHeight,
                        scale,
                        canvas: this.canvas,
                        gap: this.blockGap,
                        height: (columns > 1) ? size.height : null,
                        columns,
                        columnGap
                    });
                    return {
                        isFit: containerSize.height <= size.height,
                        scaledContainerSize: containerSize,
                        scaledBlockProps: blockProps
                    };
                }
            });

            isTextFit = isFit;
            containerSize = scaledContainerSize;
            blockProps = scaledBlockProps;
            textScale = scale;
        } else {
            // calculate the block props
            ({ containerSize, blockProps } = calcAuthoringBlockProps({
                element: this,
                model: this.textModel,
                width: size.width,
                autoWidth, autoHeight,
                scale: 1,
                canvas: this.canvas,
                gap: this.blockGap,
                height: (columns > 1) ? size.height : null,
                columns,
                columnGap
            }));
        }

        let y = 10;
        for (let block of blockProps) {
            let index = blockProps.indexOf(block);

            if (block.type == AuthoringBlockType.ELEMENT) {
                // if (block.isVisible) {
                block.blockElement = this.blockElements[block.id];

                block.blockElementProps = block.blockElement.calcProps(new geom.Size(block.bounds.width, block.model.blockHeight ?? 300));
                block.blockElementProps.bounds = new geom.Rect(block.bounds.left, block.bounds.top, block.blockElementProps.size);

                if (block.model.backgroundColor) {
                    block.blockElement.getBackgroundColor = () => {
                        return this.canvas.getTheme().palette.getColor(block.model.backgroundColor);
                    };
                }
            }
            y += block.bounds.height;

            if (index > 0) {
                let blockSpacing = this.blockGap ?? 0;

                let prevBlock = blockProps[index - 1];
                blockSpacing += (prevBlock.textStyles?.marginBottom ?? 0) + (block.textStyles?.marginTop ?? 0);
                blockSpacing -= (prevBlock.bottomSpace ?? 0) + (block.topSpace ?? 0);

                y += blockSpacing;
            }

            block.canEdit = this.canEdit;
            block.allowAdvancedTextStyling = this.blockStructure === BlockStructureType.FREEFORM;

            // if (this.allowListStyling && block.textStyle.equalsAnyOf(TextStyleType.BODY, TextStyleType.BULLET_LIST, TextStyleType.NUMBERED_LIST)) {
            //     block.allowListStyling = true;
            // }

            if (this.blockStructure == BlockStructureType.HEADER || this.blockStructure == BlockStructureType.TITLE_AND_BODY) {
                // remove non-title and non-header blocks when they are empty
                if (block.textStyle.equalsAnyOf(TextStyleType.LABEL, TextStyleType.BODY)) {
                    block.removeBlockWhenEmpty = true;
                }
            }

            // lock the first block to prevent dragging and deletion when we are using a structure block setting
            if (this.blockStructure != BlockStructureType.FREEFORM && block.isFirstBlock) {
                block.isLocked = true;
            }
        }

        let calculatedSize = size.clone();
        if (autoWidth) {
            calculatedSize.width = containerSize.width;
        }
        if (autoHeight) {
            calculatedSize.height = containerSize.height;
        }

        let textOffsetY = 0;
        if (autoHeight) {
            // offset the inner text div to align the top of the "H" character with the top of the bounds and adjust the calculated height to align with the bottom of the H char
            textOffsetY = blockProps[0].topSpace;
            calculatedSize.height -= (_.last(blockProps).bottomSpace ?? 0) + (blockProps[0].topSpace ?? 0);
        }

        return {
            size: calculatedSize,
            isTextFit,
            originalTextSize: calculatedSize.clone(),   // this is only used for animation on ArrowBars, be nice to figure out a better way to handle this
            textScale,
            textBounds: new geom.Rect(0, 0, containerSize),
            blockProps,
            textAlign: this.textAlign,
            verticalAlign: this.verticalAlign,
            columns,
            columnGap,
            textOffsetY
        };
    }

    get canRefreshElement() {
        return false;
    }

    refreshElement(transition, suppressRefreshSelectionLayer) {
        // this.canvas.refreshElement(this, transition);
    }

    handleChildElementVisibilityChanged = (blockId, isVisible) => {
        let block = this.calculatedProps.blockProps.findById(blockId);
        //
        // if (isVisible) {
        //     block.blockElement = this.blockElements[block.id];
        //     console.log(blockId, isVisible);
        //     block.blockElementProps = block.blockElement.calcProps(new geom.Size(block.bounds.width, block.model.blockHeight || 300));
        //     block.blockElementProps.bounds = new geom.Rect(block.bounds.left, block.bounds.top, block.blockElementProps.size);
        //
        //     this.refreshRender();
        // }
    }

    renderChildren(transition) {
        let props = this.calculatedProps;

        // apply resolved color styles to our blockProps.textStyles
        for (let block of props.blockProps) {
            if (block.type == AuthoringBlockType.TEXT) {
                if (block.model.fontColor) { // if the block overriddes it's color, use the block model's font color
                    block.textStyles.color = this.canvas.getTheme().palette.getColor(block.model.fontColor).toRgbString();
                } else { // otherwise, get the already resolved color from styles
                    switch (block.model.blockStyle) {
                        case BlockStyleType.COLOR:
                            let blockColor = this.canvas.getTheme().palette.getColor(block.model.blockColor);
                            block.textStyles.color = this.canvas.getTheme().palette.getForeColor(block.textStyles.fontColor, null, blockColor).toRgbString();
                            break;
                        case BlockStyleType.INSET:
                            block.textStyles.color = this.canvas.getTheme().palette.getForeColor(block.textStyles.fontColor, null, "white").toRgbString();
                            break;
                        case BlockStyleType.NONE:
                        default:
                            block.textStyles.color = (this.styles[block.model.textStyle || block.id || this.id] || this.styles).resolved_fontColor?.toRgbString();
                    }
                }
            }
        }

        let containerStyles = {
            top: this.styles.paddingTop,
            left: this.styles.paddingLeft,
            width: props.innerSize.width,
            height: "100%"
        };

        switch (props.verticalAlign || VerticalAlignType.TOP) {
            case VerticalAlignType.TOP:
                containerStyles.top -= props.textOffsetY;
                break;
            case VerticalAlignType.MIDDLE:
                containerStyles.top += this.innerBounds.height / 2 - props.textBounds.height / 2;
                break;
            case VerticalAlignType.BOTTOM:
                containerStyles.top += this.innerBounds.height - props.textBounds.height;
                break;
        }

        this.canvas.layouter.runPostRender(() => {
            // for (let element of Object.values(this.blockElements)) {
            //     this.intersectionObserver.observe(element.DOMNode);
            // }
        });

        return [
            <TextBlockContainer key={this.id} style={containerStyles}>
                <AuthoringBlockContainer
                    element={this}
                    ref={this.blockContainerRef}
                    isTextFit={true}
                    model={this.textModel}
                    blockProps={props.blockProps}
                    gap={this.blockGap}
                    scale={props.textScale}
                    canvas={this.canvas}
                    columns={props.columns}
                    columnGap={props.columnGap}
                    onChildElementChangedVisible={this.handleChildElementVisibilityChanged}
                />
            </TextBlockContainer>
        ];
    }

    getBackgroundColor(forElement) {
        if (forElement?.options.blockId) {
            let block = this.getBlockById(forElement.options.blockId);
            if (block && block.blockStyle == "color") {
                return this.canvas.getTheme().palette.getColor(block.blockColor);
            } else {
                return super.getBackgroundColor(forElement);
            }
        } else {
            return super.getBackgroundColor(forElement);
        }
    }
}

class DocumentElementContainer extends BaseElement {
    get _canSelect() {
        return false;
    }

    _build() {
        let elementClass = this.canvas.elementManager.get(this.model.elementType);
        let element = this.addElement("element", () => elementClass, {
            model: this.model,
            blockId: this.options.blockId,
            // canSelect: false
        });
    }
}
